/**
 * AI Assistant API wrappers (chat-api.ts precedent: bare typed functions over
 * the shared axios instance; TanStack Query wiring lives in the page).
 *
 * `sendAssistantMessage` deliberately returns a TYPED RESULT instead of
 * throwing: assistant failures carry a `fallback` flag that tells the UI to
 * show the scripted help layer, and returning (not throwing) keeps the global
 * MutationCache error toast out of the thread (errors render inline).
 */
import { BASE_URL, api } from './api';
import { useAuthStore } from './auth-store';

export type AssistantIntakeQuestionDto = {
  key: string;
  question: string;
  placeholder?: string;
  /** This member's saved answer (null = not answered yet). */
  answer: string | null;
};

export type AssistantPersonaDto = {
  id: string;
  name: string;
  icon: string;
  tagline: string;
  /** True for personas that can read the member's own account data. */
  accountAware: boolean;
  /** BCP-47 hint for the read-aloud voice (null = browser default). */
  voiceHint: string | null;
  /** Profile questions this persona asks up front, with the member's answers. */
  intake: AssistantIntakeQuestionDto[];
  /** Starter chips; `{key}` placeholders reference intake answers. */
  suggestedQuestions: string[];
  /** Member pressed "don't ask again" on this persona's intake card. */
  intakeSkipped: boolean;
};

export type AssistantConversationDto = {
  id: string;
  personaId: string;
  title: string | null;
  lastMessageAt: string | null;
  createdAt: string;
};

export type AssistantMessageMetadata = {
  model?: string;
  refusal?: boolean;
  truncated?: boolean;
  /** Ran out of tool-loop iterations mid-answer. */
  incomplete?: boolean;
  citations?: Array<{ url: string; title: string | null }>;
  navigate?: Array<{ route: string; label: string }>;
};

export type AssistantMessageDto = {
  id: string;
  role: 'user' | 'assistant';
  body: string;
  metadata: AssistantMessageMetadata;
  /** This member's rating of the answer (null = not rated). */
  feedback?: 'up' | 'down' | null;
  createdAt: string;
};

export type SendAssistantResult =
  | { ok: true; message: AssistantMessageDto; capRemaining: number | null }
  | { ok: false; error: string; fallback: boolean };

/**
 * Cross-chat recall state. `tenantEnabled` false = the platform doesn't offer
 * it at all, so the member control is hidden rather than shown disabled.
 */
export type AssistantRecallState = { tenantEnabled: boolean; optedOut: boolean };

export async function listAssistantPersonas(): Promise<{
  personas: AssistantPersonaDto[];
  capRemaining: number | null;
  recall: AssistantRecallState;
}> {
  const res = await api.get<{
    personas: AssistantPersonaDto[];
    capRemaining: number | null;
    recall?: AssistantRecallState;
  }>('/assistant/personas');
  return {
    personas: res.data.personas,
    capRemaining: res.data.capRemaining ?? null,
    recall: res.data.recall ?? { tenantEnabled: false, optedOut: false },
  };
}

/**
 * Member's own recall switch. Turning it off also erases the summaries already
 * written for them (the server reports how many).
 */
export async function setAssistantRecall(recallEnabled: boolean): Promise<{
  recallEnabled: boolean;
  clearedDigests: number;
}> {
  const res = await api.patch<{ recallEnabled: boolean; clearedDigests: number }>(
    '/assistant/preferences',
    { recallEnabled },
  );
  return res.data;
}

export async function listAssistantConversations(): Promise<AssistantConversationDto[]> {
  const res = await api.get<{ conversations: AssistantConversationDto[] }>(
    '/assistant/conversations',
    { params: { limit: 50 } },
  );
  return res.data.conversations;
}

export async function createAssistantConversation(
  personaId: string,
): Promise<AssistantConversationDto> {
  const res = await api.post<AssistantConversationDto>('/assistant/conversations', { personaId });
  return res.data;
}

export async function listAssistantMessages(
  conversationId: string,
  opts: { limit?: number; cursor?: string } = {},
): Promise<{ messages: AssistantMessageDto[] }> {
  const res = await api.get<{ messages: AssistantMessageDto[] }>(
    `/assistant/conversations/${conversationId}/messages`,
    { params: { limit: opts.limit ?? 30, cursor: opts.cursor } },
  );
  return res.data;
}

export async function sendAssistantMessage(
  conversationId: string,
  body: string,
): Promise<SendAssistantResult> {
  try {
    const res = await api.post<{ message: AssistantMessageDto; capRemaining: number | null }>(
      `/assistant/conversations/${conversationId}/messages`,
      { body },
    );
    return { ok: true, ...res.data };
  } catch (e) {
    const data = (e as { response?: { data?: { error?: string; fallback?: boolean } } }).response
      ?.data;
    return {
      ok: false,
      error: data?.error ?? 'network_error',
      fallback: Boolean(data?.fallback),
    };
  }
}

/** Re-answer the last question, replacing the answer the member didn't like. */
export async function regenerateAssistantMessage(
  conversationId: string,
): Promise<SendAssistantResult> {
  try {
    const res = await api.post<{ message: AssistantMessageDto; capRemaining: number | null }>(
      `/assistant/conversations/${conversationId}/regenerate`,
    );
    return { ok: true, ...res.data };
  } catch (e) {
    const data = (e as { response?: { data?: { error?: string; fallback?: boolean } } }).response
      ?.data;
    return {
      ok: false,
      error: data?.error ?? 'network_error',
      fallback: Boolean(data?.fallback),
    };
  }
}

export async function renameAssistantConversation(
  conversationId: string,
  title: string,
): Promise<{ id: string; title: string }> {
  const res = await api.patch<{ id: string; title: string }>(
    `/assistant/conversations/${conversationId}`,
    { title },
  );
  return res.data;
}

/** Thumbs up/down on an answer; `null` clears an earlier rating. */
export async function setAssistantMessageFeedback(
  messageId: string,
  value: 'up' | 'down' | null,
): Promise<{ id: string; feedback: 'up' | 'down' | null }> {
  const res = await api.post<{ id: string; feedback: 'up' | 'down' | null }>(
    `/assistant/messages/${messageId}/feedback`,
    { value },
  );
  return res.data;
}

export type AssistantStreamHandlers = {
  /** The member's turn is on the server (swap the optimistic echo for this id). */
  onStart?: (e: { userMessageId: string; createdAt: string }) => void;
  onDelta?: (text: string) => void;
  onTool?: (name: string) => void;
};

/**
 * Streaming send. EventSource can't POST, so this is fetch + a hand-rolled SSE
 * reader over the response body. Same result contract as the non-streaming
 * `sendAssistantMessage`, so the caller can fall back to it transparently.
 * Aborting the signal resolves as `{ ok: false, error: 'stopped' }` — the
 * server keeps whatever it had produced.
 */
export async function sendAssistantMessageStream(
  conversationId: string,
  body: string,
  handlers: AssistantStreamHandlers = {},
  signal?: AbortSignal,
): Promise<SendAssistantResult> {
  const url = `${BASE_URL}/assistant/conversations/${conversationId}/messages/stream`;
  const request = (token: string | null) =>
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ body }),
    });

  try {
    let res = await request(useAuthStore.getState().token);
    if (res.status === 401) {
      // Mirror the axios interceptor's one-shot refresh-and-retry.
      const refreshed = await refreshAccessToken();
      if (!refreshed) return { ok: false, error: 'unauthorized', fallback: false };
      res = await request(refreshed);
    }

    // Gates answer with JSON before the stream opens (and so can a proxy).
    if (!res.ok || !res.headers.get('content-type')?.includes('text/event-stream')) {
      const data = (await res.json().catch(() => null)) as {
        error?: string;
        fallback?: boolean;
      } | null;
      return {
        ok: false,
        error: data?.error ?? 'network_error',
        fallback: Boolean(data?.fallback),
      };
    }
    if (!res.body) return { ok: false, error: 'network_error', fallback: false };

    let result: SendAssistantResult | null = null;
    for await (const frame of readSseFrames(res.body)) {
      switch (frame.event) {
        case 'start':
          handlers.onStart?.(frame.data as { userMessageId: string; createdAt: string });
          break;
        case 'delta':
          handlers.onDelta?.((frame.data as { text: string }).text);
          break;
        case 'tool':
          handlers.onTool?.((frame.data as { name: string }).name);
          break;
        case 'done': {
          const d = frame.data as { message: AssistantMessageDto; capRemaining: number | null };
          result = { ok: true, message: d.message, capRemaining: d.capRemaining };
          break;
        }
        case 'error': {
          const d = frame.data as { error?: string; fallback?: boolean };
          result = {
            ok: false,
            error: d.error ?? 'assistant_upstream_error',
            fallback: Boolean(d.fallback),
          };
          break;
        }
      }
    }
    // The connection dropped mid-answer. Distinct from `network_error` (which
    // means the request never got off the ground) because by this point the
    // server already persisted the member's turn — retrying would duplicate it.
    return result ?? { ok: false, error: 'stream_incomplete', fallback: false };
  } catch (e) {
    if (signal?.aborted || (e as Error)?.name === 'AbortError') {
      return { ok: false, error: 'stopped', fallback: false };
    }
    return { ok: false, error: 'network_error', fallback: false };
  }
}

/** Yield `{event, data}` frames from an SSE body, skipping keep-alive comments. */
async function* readSseFrames(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<{ event: string; data: unknown }> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let split = buffer.indexOf('\n\n');
      while (split !== -1) {
        const chunk = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        const frame = parseSseChunk(chunk);
        if (frame) yield frame;
        split = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseSseChunk(chunk: string): { event: string; data: unknown } | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of chunk.split('\n')) {
    if (line.startsWith(':')) continue; // keep-alive
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (!dataLines.length) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('\n')) };
  } catch {
    return null;
  }
}

/** One-shot refresh for the streaming path (axios owns the interceptor path). */
async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await api.post<{ accessToken: string }>('/auth/refresh', {});
    const token = res.data.accessToken;
    const user = useAuthStore.getState().user;
    if (user) useAuthStore.getState().setSession(token, user);
    return token;
  } catch {
    return null;
  }
}

export async function saveAssistantIntake(
  personaId: string,
  payload: { answers?: Record<string, string>; skip?: boolean },
): Promise<{ personaId: string; answers: Record<string, string>; intakeSkipped: boolean }> {
  const res = await api.post<{
    personaId: string;
    answers: Record<string, string>;
    intakeSkipped: boolean;
  }>(`/assistant/personas/${personaId}/intake`, payload);
  return res.data;
}

export async function deleteAssistantConversation(conversationId: string): Promise<void> {
  await api.delete(`/assistant/conversations/${conversationId}`);
}

export async function clearAssistantMemory(): Promise<{ deleted: number }> {
  const res = await api.delete<{ deleted: number }>('/assistant/memory');
  return res.data;
}
